const DEFAULT_MARKETING_SITE = 'https://nexvill.ai';
const LOCAL_MARKETING_SITE = 'http://localhost:5180';

/**
 * Map a chat-app (or related) hostname to the marketing-site origin for that env.
 * Returns null when the hostname is not a known NexVill app host.
 */
export function marketingSiteUrlForHostname(hostname: string | null | undefined): string | null {
  const host = (hostname ?? '').trim().toLowerCase();
  if (!host) {
    return null;
  }

  if (host === 'app.nexvill.ai' || host === 'nexvill.ai' || host === 'www.nexvill.ai') {
    return DEFAULT_MARKETING_SITE;
  }
  if (
    host === 'parentzone.app.nexvill.ai' ||
    host === 'report.app.nexvill.ai' ||
    host === 'admin.app.nexvill.ai'
  ) {
    return DEFAULT_MARKETING_SITE;
  }

  if (
    host === 'app.preprod.nexvill.ai' ||
    host === 'preprod.nexvill.ai' ||
    host === 'www.preprod.nexvill.ai' ||
    host.endsWith('.preprod.nexvill.ai')
  ) {
    return 'https://preprod.nexvill.ai';
  }

  if (
    host === 'app.dev.nexvill.ai' ||
    host === 'dev.nexvill.ai' ||
    host === 'www.dev.nexvill.ai' ||
    host.endsWith('.dev.nexvill.ai')
  ) {
    return 'https://dev.nexvill.ai';
  }

  return null;
}

/** Marketing site origin (nexvill.ai in prod, site dev server locally). */
export function getMarketingSiteUrl(): string {
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return LOCAL_MARKETING_SITE;
    }
    const fromHost = marketingSiteUrlForHostname(hostname);
    if (fromHost) {
      return fromHost;
    }
  }

  const fromEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.VITE_MARKETING_SITE_URL;
  if (fromEnv?.trim()) {
    return fromEnv.trim().replace(/\/+$/, '');
  }

  return DEFAULT_MARKETING_SITE;
}

export function getMarketingLoginUrl(): string {
  return `${getMarketingSiteUrl()}/login`;
}

export function getMarketingSignupUrl(): string {
  return `${getMarketingSiteUrl()}/signup`;
}

export function isMarketingAuthPage(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const base = getMarketingSiteUrl();
  try {
    const marketing = new URL(base);
    const current = window.location;
    if (marketing.origin !== current.origin) {
      return false;
    }
    return current.pathname === '/login' || current.pathname === '/signup';
  } catch {
    return false;
  }
}
